/**
 * "New from template": the starter templates (GET /api/v1/templates) as cards, plus a blank graphic. Used on the
 * start screen and, inside PRISM, when PRISM asks for a new graphic (host:init mode 'new'). The workspace's brand
 * kit is applied to the copy unless the user turns it off. A template has a layout per shape (ADR 0036, 0037): the
 * cards show the shapes as badges, and those with a layout for the chosen shape (or all of them with "All shapes";
 * another shape is adapted from the primary).
 */
import { useEffect, useState } from 'react';
import { FilePlus2, LayoutTemplate, MapPin, Stamp, Type, UserRound } from 'lucide-react';
import type { Aspect } from '@prism-graphics/scene-schema';
import { api, type TemplateSummary } from './api.ts';
import { t } from './i18n.ts';
import { aspectLabel } from './ui/formats.tsx';

const ICONS: Record<string, typeof Type> = { 'lower-third': UserRound, title: Type, locator: MapPin, logo: Stamp };

export function TemplatePicker({ onPick, onBlank, busy, brandAvailable, aspect }: {
  onPick: (id: string, brand: boolean) => void;
  onBlank?: () => void;
  busy?: boolean;
  brandAvailable?: boolean;
  /** Show this shape's templates (all of them for 'other' or when absent). */
  aspect?: Aspect;
}) {
  const [items, setItems] = useState<TemplateSummary[]>();
  const [error, setError] = useState<string>();
  const [brand, setBrand] = useState(true);
  const [all, setAll] = useState(false);
  useEffect(() => {
    api.templates().then((r) => setItems(r.items), (e: Error) => setError(e.message));
  }, []);
  const filtered = aspect !== undefined && aspect !== 'other' && !all;
  const shown = (items ?? []).filter((tpl) => !filtered || tpl.layouts.includes(aspect));
  return (
    <div className="template-picker" data-testid="template-picker" data-aspect={filtered ? aspect : 'all'}>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {aspect !== undefined && aspect !== 'other' && (
        <label className="toggle-row">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} data-testid="template-all-shapes" /> {t('templates.allShapes', { shape: aspectLabel(aspect) })}
        </label>
      )}
      <ul className="template-grid">
        {onBlank && (
          <li>
            <button type="button" className="template-card" onClick={onBlank} disabled={busy} data-testid="template-blank">
              <FilePlus2 size={28} aria-hidden className="tpl-icon" />
              <span className="li-title">{t('templates.blank')}</span>
              <span className="li-meta">{t('templates.blankHint')}</span>
            </button>
          </li>
        )}
        {shown.map((tpl) => {
          const Icon = ICONS[tpl.category] ?? LayoutTemplate;
          return (
            <li key={tpl.id}>
              <button type="button" className="template-card" onClick={() => onPick(tpl.id, brand)} disabled={busy} data-testid={`template-${tpl.id}`} title={tpl.description}>
                <Icon size={28} aria-hidden className="tpl-icon" />
                <span className="li-title">{tpl.name}</span>
                <span className="li-meta">{tpl.description}</span>
                <span className="li-meta small">
                  <span className="layout-badges" data-testid="template-layouts">
                    {tpl.layouts
                      .filter((a) => a !== 'other')
                      .map((a) => (
                        <span key={a} className="aspect-badge" data-testid="aspect-badge" data-aspect={a}>
                          {a}
                        </span>
                      ))}
                  </span>{' '}
                  {t('templates.fields', { n: tpl.fields.length })}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {items === undefined && !error && <p className="muted">{t('editor.loading')}</p>}
      {brandAvailable && (
        <label className="toggle-row">
          <input type="checkbox" checked={brand} onChange={(e) => setBrand(e.target.checked)} data-testid="template-brand" /> {t('templates.applyBrand')}
        </label>
      )}
    </div>
  );
}
